import React, { FunctionComponent, useRef, useState } from 'react';
import { Box, createStyles, makeStyles, Theme, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import clsx from 'clsx';
import { faFileUpload } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MobilePhotoSourceSheet } from 'components/UI';
import { MAX_IMAGE_UPLOAD_BYTES } from 'utils/ImageResizeUtils';
import { isJpgPngOnlyFormatAllowed, normalizeImageToJpegIfFormatNotAllowed } from 'utils/imageFormatToJpeg';
import { getGalleryFileAccept, isIOSDevice } from 'utils/mobileImageGalleryAcept';

interface ImageSelectorProps {
    title: JSX.Element;
    image?: string;
    onSelect: (file: File) => void;
    className?: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column'
        },
        imageContainer: {
            height: 175,
            width: '100%',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 20,
            overflow: 'hidden',

            [theme.breakpoints.down('sm')]: {
                border: '2px dashed #000000'
            },
            [theme.breakpoints.up('md')]: {
                width: '450px',
                height: '107px',
                backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='30' ry='30' stroke='%23222222FF' stroke-width='1' stroke-dasharray='10%2c10' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`
            },
            [theme.breakpoints.only('lg')]: {
                width: '525px',
                height: '142px'
            },
            [theme.breakpoints.up('xl')]: {
                width: '750px',
                height: '215px'
            }
        },
        uploadContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            [theme.breakpoints.down('lg')]: {
                maxHeight: '100%'
            },
            [theme.breakpoints.up('xl')]: {
                maxHeight: '100%'
            }
        },
        titleContainer: {
            'fontSize': 14,
            'lineHeight': 1,
            'fontWeight': 600,

            [theme.breakpoints.up('xl')]: {
                fontSize: 20,
                fontWeight: 400
            },

            '& strong': {
                fontWeight: 600
            }
        },
        iconContainer: {
            marginTop: 12,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',

            [theme.breakpoints.up('md')]: {
                marginTop: '22px'
            },
            [theme.breakpoints.up('xl')]: {
                marginTop: '34px'
            }
        },
        uploadTextContainer: {},
        uploadIcon: {
            fontSize: 30,
            marginBottom: 6,
            color: '#5E8677',

            [theme.breakpoints.up('md')]: {
                fontSize: 40,
                marginBottom: 16
            }
        },
        uploadText: {
            fontSize: 12,
            lineHeight: 1,
            fontWeight: 500,
            color: '#5E8677',

            [theme.breakpoints.up('md')]: {
                fontSize: 14
            }
        },
        image: {
            backgroundImage: (props: ImageSelectorProps) => 'url(' + props.image + ')',
            backgroundPosition: 'center',
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
            width: '100%',
            height: '100%'
        },
        maxSize: {
            fontSize: 14,
            lineHeight: 1,
            color: '#969696',

            [theme.breakpoints.only('md')]: {
                fontSize: '6px',
                lineHeight: '13px',
                marginLeft: '15px',
                marginRight: '10px'
            },
            [theme.breakpoints.only('lg')]: {
                fontSize: '8px',
                lineHeight: '18px',
                marginLeft: '20px',
                marginRight: '13px'
            },
            [theme.breakpoints.only('xl')]: {
                fontSize: '12px',
                lineHeight: '27px',
                marginLeft: '30px',
                marginRight: '20px'
            }
        },
        warningSize: {
            color: 'red',
            [theme.breakpoints.down('sm')]: {
                fontSize: '4px',
                lineHeight: '8px'
            },
            [theme.breakpoints.only('md')]: {
                fontSize: '6px',
                lineHeight: '13px'
            },
            [theme.breakpoints.only('lg')]: {
                fontSize: '8px',
                lineHeight: '18px'
            },
            [theme.breakpoints.only('xl')]: {
                fontSize: '12px',
                lineHeight: '27px'
            }
        },
        infoContainer: {
            display: 'flex',

            [theme.breakpoints.down('sm')]: {
                marginTop: 12
            }
        }
    })
);

const DESKTOP_ACCEPT = '.png,.jpg,.jpeg';

export const ImageSelector: FunctionComponent<ImageSelectorProps> = props => {
    const classes = useStyles(props);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTouchMobile =
        typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
    const useActionsSheet = isMobile || isTouchMobile;

    const [warningKind, setWarningKind] = useState<'none' | 'format' | 'upload'>('none');
    const [showActionsSheet, setShowActionsSheet] = useState(false);

    const desktopInputRef = useRef<HTMLInputElement>(null);
    const galleryInputRef = useRef<HTMLInputElement>(null);
    const cameraInputRef = useRef<HTMLInputElement>(null);

    const handleImageClick = () => {
        if (isIOSDevice()) {
            openGalleryPicker();
            return;
        }
        if (useActionsSheet) {
            setShowActionsSheet(true);
        } else {
            desktopInputRef.current?.click();
        }
    };

    const openGalleryPicker = () => {
        galleryInputRef.current?.click();
        setShowActionsSheet(false);
    };

    const openCameraPicker = () => {
        cameraInputRef.current?.click();
        setShowActionsSheet(false);
    };

    const selectImageHandler = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputEl = event.target;
        const rawFile = inputEl.files?.[0];
        inputEl.value = '';

        if (!rawFile) {
            return;
        }

        if (rawFile.size > MAX_IMAGE_UPLOAD_BYTES) {
            setWarningKind('upload');
            return;
        }

        let file: File;
        try {
            file = await normalizeImageToJpegIfFormatNotAllowed(rawFile, isJpgPngOnlyFormatAllowed);
        } catch {
            setWarningKind('format');
            return;
        }

        if (file.size > MAX_IMAGE_UPLOAD_BYTES) {
            setWarningKind('upload');
            return;
        }

        setWarningKind('none');
        props.onSelect(file);
    };

    return (
        <Box
            className={clsx(classes.container, props.className)}
            style={{ position: 'relative' }}
        >
            {useActionsSheet ? (
                <>
                    <input
                        style={{
                            position: 'absolute',
                            width: 0,
                            height: 0,
                            opacity: 0,
                            overflow: 'hidden'
                        }}
                        type="file"
                        ref={galleryInputRef}
                        onChange={selectImageHandler}
                        accept={isIOSDevice() ? getGalleryFileAccept() : DESKTOP_ACCEPT}
                        aria-hidden
                        tabIndex={-1}
                    />
                    <input
                        style={{
                            position: 'absolute',
                            width: 0,
                            height: 0,
                            opacity: 0,
                            overflow: 'hidden'
                        }}
                        type="file"
                        ref={cameraInputRef}
                        onChange={selectImageHandler}
                        accept="image/*"
                        capture="environment"
                        aria-hidden
                        tabIndex={-1}
                    />
                </>
            ) : (
                <input
                    style={{ display: 'none' }}
                    type="file"
                    ref={desktopInputRef}
                    onChange={selectImageHandler}
                    accept={DESKTOP_ACCEPT}
                    value=""
                />
            )}
            <Box className={clsx(classes.imageContainer, props.className)}>
                {props.image ? (
                    <Box
                        className={classes.image}
                        onClick={handleImageClick}
                    />
                ) : (
                    <Box
                        className={classes.uploadContainer}
                        onClick={() => handleImageClick()}
                    >
                        <Box className={classes.titleContainer}>{props.title}</Box>
                        <Box className={classes.iconContainer}>
                            <FontAwesomeIcon
                                icon={faFileUpload}
                                className={classes.uploadIcon}
                            />
                        </Box>
                        <Box className={classes.uploadTextContainer}>
                            <Typography className={classes.uploadText}>Upload</Typography>
                        </Box>
                    </Box>
                )}
            </Box>
            <Box className={classes.infoContainer}>
                <Box
                    display="flex"
                    flexDirection="column"
                >
                    <Typography className={classes.maxSize}>
                        {`Max size ${MAX_IMAGE_UPLOAD_BYTES / (1024 * 1024)}MB`}
                    </Typography>
                </Box>
                {warningKind === 'upload' && (
                    <Typography className={classes.warningSize}>
                        {`Image exceeds ${MAX_IMAGE_UPLOAD_BYTES / (1024 * 1024)}MB upload limit.`}
                    </Typography>
                )}
                {warningKind === 'format' && (
                    <Typography className={classes.warningSize}>Could not load image. Use JPG or PNG.</Typography>
                )}
            </Box>
            {useActionsSheet && (
                <MobilePhotoSourceSheet
                    open={showActionsSheet}
                    onClose={() => setShowActionsSheet(false)}
                    onPhotoGallery={openGalleryPicker}
                    onCamera={openCameraPicker}
                />
            )}
        </Box>
    );
};

export default ImageSelector;
